import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy } from 'lucide-react';
import { aiInterviewsAPI, errMsg } from '../../../lib/aiInterviewApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { copyText, Loading, StatusPill } from './common';

const ACTIVE = ['invited', 'opened', 'consented', 'in_progress', 'interrupted'];
const RESULT_LABEL = { created: 'Invite emailed', created_no_email: 'Created — no email on file, share the link', duplicate: 'Already has an active AI interview for this job', not_found: 'Not found', wrong_job: 'Belongs to another job' };

const Results = ({ results, names, onClose }) => (
  <div data-testid="invite-results">
    <div style={{ display: 'grid', gap: '0.5rem', maxHeight: 360, overflowY: 'auto' }}>
      {results.map((r) => (
        <div key={r.candidate_id} className="iv-status" data-testid={`invite-result-${r.candidate_id}`}>
          <span><b>{names[r.candidate_id] || r.candidate_id}</b><span className="iv-small" style={{ display: 'block' }}>{RESULT_LABEL[r.status] || r.status}</span></span>
          {r.link && <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => copyText(r.link, toast)} data-testid={`invite-copy-link-${r.candidate_id}`}><Copy size={14} /> Copy link</button>}
        </div>
      ))}
    </div>
    <div className="iv-row-end"><button type="button" className="iv-btn iv-btn-primary" onClick={onClose} data-testid="invite-done-btn">Done</button></div>
  </div>
);

export default function InviteDialog({ open, onOpenChange, presetPlan, onSent }) {
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState('');
  const [cands, setCands] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const plan = presetPlan || plans.find((p) => p.id === planId);

  useEffect(() => {
    if (!open) return;
    setResults(null);
    setSel(new Set());
    if (presetPlan) { setPlanId(presetPlan.id); return; }
    aiInterviewsAPI.plans().then(({ data }) => {
      const ready = data.filter((p) => p.status === 'ready');
      setPlans(ready);
      setPlanId((cur) => cur || ready[0]?.id || '');
    }).catch(() => toast.error('Could not load plans'));
  }, [open, presetPlan]);

  useEffect(() => {
    if (!open || !plan) { setCands(null); return; }
    setCands(null);
    aiInterviewsAPI.inviteCandidates(plan.job_id, plan.id).then(({ data }) => setCands(data)).catch(() => setCands([]));
  }, [open, plan?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await aiInterviewsAPI.invite(plan.id, { candidate_ids: [...sel], expires_days: Number(days) });
      setResults(data.results);
      onSent?.();
    } catch (e) {
      toast.error(errMsg(e, 'Could not send invites'));
    } finally {
      setBusy(false);
    }
  };
  const names = Object.fromEntries((cands || []).map((c) => [c.id, c.name]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="iv max-w-2xl" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invite candidates to an AI interview</DialogTitle>
          <DialogDescription>Each candidate gets a private, expiring link by email. They consent first and can ask for a human interview instead.</DialogDescription>
        </DialogHeader>
        {results ? <Results results={results} names={names} onClose={() => onOpenChange(false)} /> : (
          <>
            {!presetPlan && (
              <select className="iv-select" value={planId} onChange={(e) => setPlanId(e.target.value)} data-testid="invite-plan-select">
                {!plans.length && <option value="">No ready plans — create one first</option>}
                {plans.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${p.job_title}`}</option>)}
              </select>
            )}
            {plan && !cands && <Loading label="Loading candidates…" />}
            {cands && !cands.length && <p className="iv-small" data-testid="invite-no-candidates">No scored candidates on this job yet. Upload résumés first.</p>}
            {cands && cands.length > 0 && (
              <div style={{ display: 'grid', gap: '0.4rem', maxHeight: 340, overflowY: 'auto' }} data-testid="invite-candidate-list">
                {cands.map((c) => {
                  const taken = ACTIVE.includes(c.invite_status);
                  return (
                    <label key={c.id} className="iv-status" style={{ cursor: taken ? 'not-allowed' : 'pointer', opacity: taken ? 0.6 : 1 }}>
                      <span className="iv-row" style={{ gap: '0.6rem' }}>
                        <input type="checkbox" disabled={taken} checked={sel.has(c.id)} onChange={() => toggle(c.id)} data-testid={`invite-candidate-checkbox-${c.id}`} />
                        <span><b>{c.name || 'Unnamed'}</b><span className="iv-small" style={{ display: 'block' }}>{c.email || 'No email — share the link manually'} · stage {c.stage}</span>
                          {taken && c.invite_plan_name && <span className="iv-small" style={{ display: 'block' }} data-testid={`invite-other-plan-${c.id}`}>{`Active interview via “${c.invite_plan_name}”`}</span>}
                        </span>
                      </span>
                      <span className="iv-row" style={{ gap: '0.4rem' }}>
                        {typeof c.score === 'number' && <span className="iv-pill iv-tone-sky">Match {Math.round(c.score)}</span>}
                        {c.invite_status && <StatusPill status={c.invite_status} />}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="iv-row" style={{ justifyContent: 'space-between', marginTop: '0.5rem' }}>
              <label className="iv-small iv-row" style={{ gap: '0.4rem' }}>Link expires in
                <input type="number" min={1} max={60} className="iv-input" style={{ width: 80 }} value={days} onChange={(e) => setDays(e.target.value)} data-testid="invite-expiry-input" /> days
              </label>
              <button type="button" className="iv-btn iv-btn-cta" disabled={!plan || !sel.size || busy} onClick={send} data-testid="invite-send-btn">
                {busy ? 'Sending…' : `Send ${sel.size || ''} invite${sel.size === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
